/**
 * PropertyEdit – edit property page with form, validation, delete dialog
 * Mirrors Angular property-edit (core info)
 */
import { Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils/helpers'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { EntityEditHeaderCard } from '@/components/ui/EntityEditHeaderCard'
import { EntityEditHeaderToolbar } from '@/components/ui/EntityEditHeaderToolbar'
import { EntityEditTabBar } from '@/components/ui/EntityEditTabBar'
import { PageHeader } from '@/features/invitee/components/PageHeader'
import { LibertyInternalNotes } from '@/features/company/components/LibertyInternalNotes'
import { usePropertyEdit } from '../hooks/usePropertyEdit'
import { PropertyEditInfoTab } from '../components/PropertyEditInfoTab'
import { PropertyEditAuditLogSection } from '../components/PropertyEditAuditLogSection'
import { PropertyEditDialogs } from '../components/PropertyEditDialogs'
import { PropertyExecutiveSummaryTab } from '../components/PropertyExecutiveSummaryTab'
import { PropertyContactsTab } from '../components/PropertyContactsTab'
import { PropertyCommunicationsTab } from '../components/PropertyCommunicationsTab'
import { PropertyPaymentsTab } from '../components/PropertyPaymentsTab'
import { PropertyIntegrationsTab } from '../components/PropertyIntegrationsTab'
import { PropertyActivityTab } from '../components/PropertyActivityTab'
import { PropertyConfigTab } from '../components/PropertyConfigTab'
import { PropertyConfigSubTabs } from '../components/PropertyConfigSubTabs'
import { PropertyProductConfigTab } from '../components/PropertyProductConfigTab'
import { PropertyModelConfigTab } from '../components/PropertyModelConfigTab'
import type { PropertyTab } from '../types'

export function PropertyEdit() {
  const edit = usePropertyEdit()

  if (edit.loading) {
    return (
      <div data-testid="property-edit-loading" className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-[rgb(21_151_255)]" />
      </div>
    )
  }

  const {
    id,
    displayName,
    displayCompany,
    hasPolicy,
    propertyHeaderActionsDisabled,
    isSubmitting,
    propertyEditRelatedLinks,
    handleCancel,
    setDeleteConfirm,
    showExitUnsavedConfirm,
    handleStayOnPage,
    handleExitWithoutSaving,
    handleSaveFromExitDialog,
    onDirtyFormCapture,
    propertyEditTabItems,
    selectedTab,
    setSelectedTab,
    selectedConfigSubTab,
    setSelectedConfigSubTab,
    propertyConfigSubTabItems,
    handleSubmit,
    onSubmit,
    onFormError,
    canEditPropertyInfo,
    register,
    control,
    errors,
    watchIsMailingAddress,
    states,
    companies,
    canEditListed,
    isLibertyRole,
    isCompanySelectionLocked,
    propertyDetails,
    loadCompanies,
    addressInputRef,
    streetAddressRef,
    enableSetAddress,
    showGoogleAddress,
    streetAddress,
    setStreetAddress,
    showLatLongLookup,
    showMap,
    isMapMessage,
    mapRef,
    refreshMap,
    handleSetPlaceAddress,
    handleAddressNotFound,
    handleValidateAddress,
    handleLatLongLookup,
    isValidatingAddress,
    mapMessage,
    w9Files,
    w9Uploading,
    w9StagingName,
    w9InputRef,
    handleW9Upload,
    handleW9Download,
    setDeleteW9Confirm,
    refreshIntegrationsProperty,
    onExecutiveSummaryDataChange,
    autoOpenStaffTransfer,
    setAutoOpenStaffTransfer,
    refreshContactsProperty,
    refreshPaymentsProperty,
    propertyConfigTabRef,
    setSaveConfirm,
    refreshConfigProperty,
    propertyProductConfigTabRef,
    refreshProductConfigProperty,
    propertyModelConfigTabRef,
    refreshModelConfigProperty,
    markPropertyFormDirty,
    notesRefreshKey,
    canViewAudit,
    auditRows,
    saveConfirm,
    deleteConfirm,
    duplicateAddressConfirm,
    duplicateProperties,
    companyChangeConfirm,
    deleteW9Confirm,
    transferOptionOpen,
    showAddressValidationModal,
    setShowAddressValidationModal,
    addressValidationModalMessage,
    addressValidationModalTitle,
    handleConfirmSave,
    handleDelete,
    handleDuplicateSave,
    setDuplicateAddressConfirm,
    handleCompanyChangeConfirmOk,
    setCompanyChangeConfirm,
    executeW9Delete,
    setTransferOptionOpen,
  } = edit

  return (
    <div data-testid="property-edit-page" className="w-full min-h-screen bg-slate-50/50 font-sans text-slate-900 pb-12">
      <PageHeader
        title="EDIT PROPERTY"
        testIdRoot="property-edit-page-header"
        impersonationStopDataTestId="property-edit-stop-impersonate"
      />

      <EntityEditHeaderCard
        lead={
          <>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 sm:text-2xl break-words">
                {displayName || 'Edit property'}
              </h2>
              {displayCompany ? (
                <span className="text-sm font-medium text-slate-600 sm:text-base">
                  managed by {displayCompany}
                </span>
              ) : null}
            </div>
            <p className="mt-1 text-sm text-slate-500">
              Property ID <span className="font-medium tabular-nums text-slate-700">{id}</span>
            </p>
          </>
        }
        toolbar={
          hasPolicy(['property_info_edit']) ? (
            <EntityEditHeaderToolbar
              headerActionsDisabled={propertyHeaderActionsDisabled}
              isHeaderSaveLoading={isSubmitting}
              isDeleting={false}
              canEdit
              canDelete={hasPolicy(['property_delete'])}
              relatedLinks={propertyEditRelatedLinks}
              save={{ type: 'submit', formId: 'property-edit-form', testId: 'property-edit-submit-button' }}
              cancelLabel="Exit"
              cancelTestId="property-edit-cancel-button"
              deleteTestId="property-edit-delete-button"
              onCancel={handleCancel}
              onDelete={() => {
                if (propertyHeaderActionsDisabled) return
                setDeleteConfirm(true)
              }}
            />
          ) : null
        }
      />

      <div className="mx-4 my-4 sm:mx-6">
        <div className="bg-white shadow-sm border border-slate-200 overflow-visible rounded-lg ring-1 ring-slate-100">
          <EntityEditTabBar
            navTestId="property-edit-tab-nav"
            tabs={propertyEditTabItems}
            activeTab={selectedTab}
            onSelect={(tabId) => setSelectedTab(tabId as PropertyTab)}
          />

          <div
            className="min-h-[150px] sm:min-h-[200px] w-full min-w-0 p-4 sm:p-6 bg-slate-50/50"
            onInputCapture={onDirtyFormCapture}
            onChangeCapture={onDirtyFormCapture}
          >
            <form
              id="property-edit-form"
              data-testid="property-edit-form"
              onSubmit={handleSubmit(onSubmit, onFormError)}
              className="w-full min-w-0 space-y-6 sm:space-y-8 text-[14px] leading-normal"
            >
              <div
                className={cn(
                  selectedTab !== 'propertyinfo'
                    ? 'hidden'
                    : `${tabStyles.tabContent} animate-in fade-in duration-300`,
                  !canEditPropertyInfo && 'pointer-events-none select-none opacity-[0.92]',
                )}
              >
                <PropertyEditInfoTab
                  register={register}
                  control={control}
                  errors={errors}
                  watchIsMailingAddress={!!watchIsMailingAddress}
                  states={states}
                  companies={companies}
                  canEditPropertyInfo={canEditPropertyInfo}
                  canEditListed={canEditListed}
                  isLibertyRole={isLibertyRole}
                  isCompanySelectionLocked={isCompanySelectionLocked}
                  propertyDetails={propertyDetails}
                  propertyId={id}
                  loadCompanies={loadCompanies}
                  addressInputRef={addressInputRef}
                  streetAddressRef={streetAddressRef}
                  enableSetAddress={enableSetAddress}
                  showGoogleAddress={showGoogleAddress}
                  streetAddress={streetAddress}
                  setStreetAddress={setStreetAddress}
                  showLatLongLookup={showLatLongLookup}
                  showMap={showMap}
                  isMapMessage={isMapMessage}
                  mapRef={mapRef}
                  refreshMap={refreshMap}
                  onSetPlaceAddress={handleSetPlaceAddress}
                  onAddressNotFound={handleAddressNotFound}
                  onValidateAddress={handleValidateAddress}
                  onLatLongLookup={handleLatLongLookup}
                  isValidatingAddress={isValidatingAddress}
                  mapMessage={mapMessage}
                  w9Files={w9Files}
                  w9Uploading={w9Uploading}
                  w9StagingName={w9StagingName}
                  w9InputRef={w9InputRef}
                  onW9Upload={handleW9Upload}
                  onW9Download={handleW9Download}
                  onW9DeleteRequest={(fileId) => setDeleteW9Confirm({ isOpen: true, fileId })}
                />
              </div>

              {id && (
                <div className={selectedTab !== 'communicationsinfo' ? 'hidden' : ''}>
                  <PropertyCommunicationsTab
                    control={control}
                    canEdit={hasPolicy(['property_communication_edit'])}
                  />
                </div>
              )}
              {id && (
                <div className={selectedTab !== 'integrationsinfo' ? 'hidden' : ''}>
                  <PropertyIntegrationsTab
                    control={control}
                    property={propertyDetails}
                    propertyId={id}
                    onRefresh={refreshIntegrationsProperty}
                  />
                </div>
              )}
            </form>

            {selectedTab === 'executivesummary' && (
              <PropertyExecutiveSummaryTab
                property={propertyDetails}
                propertyId={id ?? ''}
                onDataChange={onExecutiveSummaryDataChange}
              />
            )}
            {selectedTab === 'contactsinfo' && id && (
              <PropertyContactsTab
                property={propertyDetails}
                propertyId={id}
                autoOpenStaffTransfer={autoOpenStaffTransfer}
                onAutoOpenStaffTransferConsumed={() => setAutoOpenStaffTransfer(false)}
                onRefresh={refreshContactsProperty}
              />
            )}
            {selectedTab === 'bankinginfo' && (
              <PropertyPaymentsTab
                property={propertyDetails}
                propertyId={id}
                stateNameByCode={Object.fromEntries(states.map((s) => [s.state_code, s.name]))}
                onRefresh={refreshPaymentsProperty}
              />
            )}
            {selectedTab === 'activityloginfo' && id && (
              <PropertyActivityTab propertyId={id} />
            )}
            {id && (
              <div className={selectedTab !== 'configinfo' ? 'hidden' : undefined}>
                <PropertyConfigSubTabs
                  items={propertyConfigSubTabItems}
                  active={selectedConfigSubTab}
                  onSelect={setSelectedConfigSubTab}
                >
                  {hasPolicy(['yardi_config']) && (
                    <div
                      className={selectedConfigSubTab !== 'yardi' ? 'hidden' : undefined}
                      data-testid="property-config-yardi-panel"
                    >
                      <PropertyConfigTab
                        ref={propertyConfigTabRef}
                        property={propertyDetails}
                        propertyId={id}
                        onRefresh={refreshConfigProperty}
                      />
                    </div>
                  )}
                  {hasPolicy(['property_productconfig_view']) &&
                    hasPolicy(['allow_product_config']) && (
                    <div
                      className={selectedConfigSubTab !== 'product' ? 'hidden' : undefined}
                      data-testid="property-config-product-panel"
                    >
                      <PropertyProductConfigTab
                        ref={propertyProductConfigTabRef}
                        property={propertyDetails}
                        propertyId={id}
                        onRefresh={refreshProductConfigProperty}
                        onDirty={markPropertyFormDirty}
                        onRequestSave={() => {
                          const form = document.getElementById('property-edit-form') as HTMLFormElement | null
                          form?.requestSubmit()
                        }}
                      />
                    </div>
                  )}
                  {hasPolicy(['property_modelconfig_view']) && (
                    <div
                      className={selectedConfigSubTab !== 'model' ? 'hidden' : undefined}
                      data-testid="property-config-model-panel"
                    >
                      <PropertyModelConfigTab
                        ref={propertyModelConfigTabRef}
                        property={propertyDetails}
                        propertyId={id}
                        onRefresh={refreshModelConfigProperty}
                        onDirty={markPropertyFormDirty}
                        onRequestSave={() => {
                          const form = document.getElementById('property-edit-form') as HTMLFormElement | null
                          form?.requestSubmit()
                        }}
                      />
                    </div>
                  )}
                </PropertyConfigSubTabs>
              </div>
            )}
          </div>

          {id && hasPolicy(['property_notes_view']) && (
            <div
              id="property-liberty-notes"
              data-testid="property-edit-notes-section"
              className="border-t border-slate-100 bg-slate-50/50 px-3 py-4 sm:px-[30px] sm:py-6 scroll-mt-20"
            >
              <LibertyInternalNotes
                fromPage="PROPERTY"
                entityId={id}
                canEdit={hasPolicy(['property_notes_view'])}
                refreshKey={notesRefreshKey}
              />
            </div>
          )}

          {id && canViewAudit && <PropertyEditAuditLogSection auditRows={auditRows} />}
        </div>
      </div>

      {isSubmitting && (
        <div
          className="pointer-events-auto fixed inset-0 z-[10000] flex items-center justify-center bg-transparent"
          data-testid="property-edit-saving-overlay"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <Loader2 className="h-10 w-10 animate-spin text-[rgb(21_151_255)]" aria-label="Saving" />
        </div>
      )}

      <PropertyEditDialogs
        propertyId={id}
        isSubmitting={isSubmitting}
        saveConfirm={saveConfirm}
        showExitUnsavedConfirm={showExitUnsavedConfirm}
        deleteConfirm={deleteConfirm}
        duplicateAddressConfirm={duplicateAddressConfirm}
        duplicateProperties={duplicateProperties}
        companyChangeConfirm={companyChangeConfirm}
        deleteW9Confirm={deleteW9Confirm}
        transferOptionOpen={transferOptionOpen}
        showAddressValidationModal={showAddressValidationModal}
        addressValidationModalMessage={addressValidationModalMessage}
        addressValidationModalTitle={addressValidationModalTitle}
        onAddressValidationModalClose={() => setShowAddressValidationModal(false)}
        onSaveConfirm={() => void handleConfirmSave()}
        onSaveConfirmDismiss={() => setSaveConfirm(false)}
        onExitUnsavedSave={handleSaveFromExitDialog}
        onExitUnsavedDiscard={handleExitWithoutSaving}
        onExitUnsavedStay={handleStayOnPage}
        onDelete={() => void handleDelete()}
        onDeleteDismiss={() => setDeleteConfirm(false)}
        onDuplicateSave={() => void handleDuplicateSave()}
        onDuplicateDismiss={() => setDuplicateAddressConfirm(false)}
        onCompanyChangeOk={handleCompanyChangeConfirmOk}
        onCompanyChangeDismiss={() => setCompanyChangeConfirm(false)}
        onW9Delete={() => void executeW9Delete()}
        onW9DeleteDismiss={() => setDeleteW9Confirm({ isOpen: false, fileId: '' })}
        onTransferClose={() => setTransferOptionOpen(false)}
        onProceedStaffTransfer={() => {
          setSelectedTab('contactsinfo')
          setAutoOpenStaffTransfer(true)
        }}
      />
    </div>
  )
}
